import clsx from "clsx";
import { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { SubscriptionCard } from "@/components/Layout/BaseLayouts/SubscriptionCard/SubscriptionCard";
import { useSubscriptionPriceBreakdown } from "@/components/Portal/DeviceManagement/SubscriptionDetails/SubscriptionDisplay/SubscriptionPrice/useSubscriptionPriceBreakdown";
import { SimCardDetail } from "@/services/model";
import { getCurrency } from "@/utils/translationHelpers";

export type PriceRowVariant = "default" | "strong" | "strong-xs";

const priceRowTextClasses: Record<PriceRowVariant, string> = {
  default: "text-body-xs leading-md",
  strong: "text-body-base font-semibold leading-none",
  "strong-xs": "text-body-xs font-semibold leading-none tracking-[0.24px]",
};

/** One `dire-table` row: a label on the left and its amount on the right. */
export const PriceRow = ({
  label,
  value,
  variant = "default",
  withBackdrop,
  withDivider,
  withTopDivider,
  equalColumns,
}: {
  label: ReactNode;
  value: ReactNode;
  /** "strong" is the semibold styling of the product and total rows. */
  variant?: PriceRowVariant;
  withBackdrop?: boolean;
  withDivider?: boolean;
  /**
   * A dark divider line above this row, e.g. above "Summe" - independent of
   * `withDivider` on whichever row happens to precede it, so the line above
   * the total shows up regardless of which optional discount/credit rows
   * are present.
   */
  withTopDivider?: boolean;
  equalColumns?: boolean;
}) => (
  <div
    className={clsx(
      "flex w-full items-stretch",
      withBackdrop && "rounded-md bg-surface-quaternary",
      withTopDivider && "border-t border-border-primary",
    )}
  >
    <div
      className={clsx(
        // The divider sits inside the cell, on the fixed-height element: on
        // the row it would add an extra pixel. min-h, not h, so a wrapped
        // label or value can still grow the row instead of overflowing it.
        "flex min-h-14 min-w-0 flex-1 items-center px-4",
        withDivider && "border-b border-border-tertiary",
      )}
    >
      <div
        className={clsx(
          // A label without a hyphen or space (e.g. "Handynummer") has
          // nowhere to wrap by default and overflows into the value column
          // instead - break-words forces a wrap once it runs out of room.
          "min-w-0 flex-1 break-words text-foreground-primary",
          priceRowTextClasses[variant],
        )}
      >
        {label}
      </div>
    </div>
    <div
      className={clsx(
        "flex min-h-14 items-center justify-end px-4",
        // A minimum, not a fixed width: a value too wide to fit (e.g. a
        // non-wrapping phone number) must push into the label's flexible
        // column instead of overflowing past this one. Capped at half so it
        // can still shrink - and wrap, via break-words below - rather than
        // force the row wider than its container on a narrow viewport.
        equalColumns ? "flex-1" : "min-w-[120px] max-w-[50%]",
        withDivider && "border-b border-border-tertiary",
      )}
    >
      <div
        className={clsx(
          "flex flex-col items-end gap-1 break-words text-right text-foreground-primary",
          priceRowTextClasses[variant],
        )}
      >
        {value}
      </div>
    </div>
  </div>
);

/** Mint chip carrying a discounted amount (same tone as DeductionChip). */
export const DiscountChip = ({ amount }: { amount: number }) => (
  <span className="inline-flex h-6 items-center rounded-full bg-surface-accent-mint px-2 py-1 text-body-xs font-semibold leading-none text-foreground-primary">
    - {getCurrency(amount, "CHF")}
  </span>
);

/** Chip displaying a deduction (discount or credit) formatted as "- CHF X.YY". */
export const DeductionChip = ({ amount }: { amount: number }) => (
  <span className="inline-flex h-6 items-center whitespace-nowrap rounded-full bg-surface-accent-mint px-2 py-1 text-body-xs font-semibold leading-none text-foreground-primary">
    - {getCurrency(amount, "CHF")}
  </span>
);

/**
 * "Abo Grundpreis": what the subscription costs per period — base price,
 * discounts, and the add-ons (Watch SIM, Voice) listed below them.
 */
export const SubscriptionPrice = ({
  simDetail,
}: {
  simDetail: SimCardDetail;
}) => {
  const { t } = useTranslation("portal");
  const { t: tAbo } = useTranslation("portal", {
    keyPrefix: "cockpit.managementTile.subscription",
  });

  const {
    basePrice,
    discounts,
    addOns,
    totalPrice,
    isSingleRow,
    productName,
    getDiscountPeriod,
  } = useSubscriptionPriceBreakdown(simDetail);

  const interval = simDetail.autoRenew
    ? t("cockpit.managementTile.tariff.payInterval", {
        days: simDetail.minContractDuration ?? 30,
      })
    : t("cockpit.managementTile.tariff.once");

  return (
    <SubscriptionCard
      dataTestid="subscription-price"
      title={tAbo("price.title")}
    >
      <div className="flex w-full flex-col">
        <PriceRow
          withBackdrop
          equalColumns={isSingleRow}
          variant="strong"
          label={productName}
          value={
            <>
              <span>{getCurrency(basePrice, "CHF")}</span>
              {isSingleRow && (
                <span className="text-body-xs font-regular leading-md">
                  {interval}
                </span>
              )}
            </>
          }
        />

        {discounts.map(
          ({ id, amount, campaignPeriod, discountName }, index) => (
            <PriceRow
              key={id}
              withDivider={
                addOns.length === 0 && index === discounts.length - 1
              }
              label={
                <>
                  {discountName}
                  {getDiscountPeriod(campaignPeriod!) && (
                    <>
                      <br />
                      {getDiscountPeriod(campaignPeriod!)}
                    </>
                  )}
                </>
              }
              value={<DiscountChip amount={amount ?? 0} />}
            />
          ),
        )}

        {addOns.map((addOn, index) => (
          <PriceRow
            key={addOn.label}
            withDivider={index === addOns.length - 1}
            label={addOn.label}
            value={getCurrency(addOn.price, "CHF")}
          />
        ))}

        {!isSingleRow && (
          <PriceRow
            variant="strong"
            label=""
            equalColumns
            value={
              <>
                <span>{getCurrency(totalPrice, "CHF")}</span>
                <span className="text-body-xs font-regular leading-md">
                  {interval}
                </span>
              </>
            }
          />
        )}
      </div>
    </SubscriptionCard>
  );
};
